import React from "react";
import {
  Box,
  Heading,
  Link,
  List,
  ListItem,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function scrollToBlogSection(e) {
  e.preventDefault();
  const el = document.getElementById("blog-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, '', '#blog-section');
  } else {
    window.location.href = "/blog#blog-section";
  }
}

export default function FactorsThatAffectLoanApproval() {
  return (
    <Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 20, md: 32 }}>
      <VStack spacing={6} align="start">
        <Link
          as="button"
          onClick={scrollToBlogSection}
          aria-label="Back to Blog"
          position="fixed"
          top={{ base: "84px", md: "96px" }}
          left={{ base: 4, md: 8 }}
          zIndex="sticky"
          bg="white"
          color="teal.600"
          fontWeight="semibold"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="full"
          px={{ base: 0, sm: 4 }}
          py={{ base: 0, sm: 2 }}
          w={{ base: "42px", sm: "auto" }}
          h={{ base: "42px", sm: "auto" }}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
          _hover={{ textDecoration: "none", bg: "teal.50" }}
        >
          <Text as="span" display={{ base: "inline", sm: "none" }}>←</Text>
          <Text as="span" display={{ base: "none", sm: "inline" }}>← Back to Blog</Text>
        </Link>

        <Heading as="h1" size="xl" mb={2} mt={16}>10 Factors That Affect Your Loan Approval</Heading>
        <Text fontSize="sm" color="gray.500">
         By Heavenly
          •
          March 09, 2026
          •
          5 mins read
        </Text>

        <Text>Lenders review more than income when deciding whether to approve a loan.</Text>
        <Text>
          This guide explains the top 10 factors that commonly influence approval decisions and what you can improve
          before you apply.
        </Text>

        <Heading as="h2" size="md">1) Payment History</Heading>
        <Text>
          On-time payments show reliability. Late payments are one of the most common reasons approval odds decrease.
        </Text>

        <Heading as="h2" size="md">2) Debt-to-Income Ratio (DTI)</Heading>
        <Text>
          If existing debts consume too much monthly income, lenders may see repayment risk as too high.
        </Text>

        <Heading as="h2" size="md">3) Credit Utilization</Heading>
        <Text>
          High card balances relative to limits can lower approval confidence, even when payments are current.
        </Text>

        <Heading as="h2" size="md">4) Employment Stability</Heading>
        <Text>
          Consistent employment and regular direct deposit history can strengthen confidence in repayment ability.
        </Text>

        <Heading as="h2" size="md">5) Verified Income Quality</Heading>
        <Text>
          Lenders usually assess both income amount and consistency. Recent sharp drops or gaps can affect outcomes.
        </Text>

        <Heading as="h2" size="md">6) Existing Open Loans</Heading>
        <Text>
          Several active obligations can reduce affordability room for a new payment.
        </Text>

        <Heading as="h2" size="md">7) Loan Amount Requested</Heading>
        <Text>
          Asking for a realistic amount based on your profile can improve approval probability.
        </Text>

        <Heading as="h2" size="md">8) Account Conduct and Banking Signals</Heading>
        <Text>
          Frequent NSF events, repeated overdrafts, or unstable cash-flow patterns can increase risk scores.
        </Text>

        <Heading as="h2" size="md">9) Recent Credit-Seeking Activity</Heading>
        <Text>
          A burst of recent applications can indicate financial pressure and may reduce approval confidence.
        </Text>

        <Heading as="h2" size="md">10) Data Accuracy in Your Application</Heading>
        <Text>
          Mismatched personal details, incomplete forms, or inconsistent records can slow or block approvals.
        </Text>

        <Heading as="h2" size="md">How to Improve Your Approval Odds</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Pay every bill on time for at least 3-6 months</ListItem>
          <ListItem>Reduce card balances before applying</ListItem>
          <ListItem>Apply for a realistic amount</ListItem>
          <ListItem>Double-check all profile and banking details</ListItem>
          <ListItem>Avoid multiple loan applications in a short period</ListItem>
        </List>

        <Heading as="h2" size="md">Final Takeaway</Heading>
        <Text>
          Approval decisions are multi-factor. If you focus on payment consistency, manageable debt, accurate profile
          data, and realistic borrowing requests, your approval profile can improve significantly over time.
        </Text>

        <Box mt={10} width="100%" textAlign="center">
          <Heading as="h3" size="md" mb={3} color="teal.700">Get an instant loan now with Swift Credit</Heading>
          <Link as={RouterLink} to="/apply" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Box as="button" bg="teal.500" color="white" px={8} py={3} borderRadius="md" fontWeight="bold" _hover={{ bg: "teal.600" }}>
              Get a Loan
            </Box>
          </Link>
        </Box>
      </VStack>
    </Box>
  );
}